<script lang="ts">
	import type { MapPoint } from '$lib/components/ui/map/_other/mapPoint.type';
	import ControllablePopup, { PopupContent } from '$lib/components/ui/map/popup/ControllablePopup.svelte';
	import { PopupController } from '$lib/components/ui/map/popup/popup.controller.svelte';
	import type { PopupContentProps } from '$lib/components/ui/map/popup/popupProps';
	import type { ViewState } from '$lib/controllers/lastView/viewState';
	import { ViewStateController } from '$lib/controllers/lastView/viewState.controller';
	import {
		flattenCoordinates,
		lngLatToCoordinates,
		type Coordinates,
		type FlatCoordinates
	} from '$lib/features/_other/coordinates.type';
	import { CITY_FEATURE_TYPE_ICON_MAP, CityFeatureType } from '$lib/features/cityFeature/cityFeatureType.enum';
	import { MAP_LIBRE_STYLE } from '$lib/statics';
	import { snapToNearestMapCopy } from '$lib/utils/geo.util';
	import { assignMissingStyleImageResolver, getBounds, getMaxBounds } from '$lib/utils/maplibre.util';
	import type { Feature, Point } from 'geojson';
	import type { Map as LibreMap, LngLatBounds } from 'maplibre-gl';
	import Supercluster, { type ClusterFeature, type ClusterProperties, type PointFeature } from 'supercluster';
	import type { Component } from 'svelte';
	import { AttributionControl, MapLibre, Marker, NavigationControl } from 'svelte-maplibre-gl';
	import 'svelte-maplibre-gl/vite';

	interface FeatureProps {
		id: string;
		label: string;
		href: string;
		imageUrl?: string;
		type: CityFeatureType;
		badges?: string[];
	}

	type ClusterItem = ClusterFeature<ClusterProperties> | PointFeature<FeatureProps>;

	let {
		points,
		lockToArea = true,
		scrollZoom = false,
		viewState = $bindable(undefined)
	}: { points: MapPoint[]; lockToArea?: boolean; scrollZoom?: boolean; viewState?: ViewState } = $props();

	let map = $state<LibreMap | undefined>(undefined);
	let popoverController = $state<PopupController<FeatureProps> | undefined>(undefined);
	let viewStateController = $state<ViewStateController | null>(null);

	let boundsState = $state<[number, number, number, number]>([-180, -90, 180, 90]);
	let mapZoom = $state<number>(2.5);

	const bounds = $derived<LngLatBounds>(getBounds(points.map((p) => p.coords)));
	const maxBounds = $derived<LngLatBounds>(getMaxBounds(bounds, 15));
	const rawPoints = $derived.by<Feature<Point, FeatureProps>[]>(() => {
		return points.map((item) => {
			const feature: Feature<Point, FeatureProps> = {
				type: 'Feature',
				geometry: {
					type: 'Point',
					coordinates: flattenCoordinates(item.coords)
				},
				properties: {
					id: item.id,
					label: item.label,
					href: item.href,
					imageUrl: item.imageUrl,
					type: item.type,
					badges: item.badges
				}
			};
			return feature;
		});
	});
	const index = $derived.by<Supercluster<FeatureProps, ClusterProperties>>(() => {
		const sc = new Supercluster<FeatureProps, ClusterProperties>({
			radius: 30,
			maxZoom: 15
		});
		sc.load(rawPoints);
		return sc;
	});
	const clusters = $derived<ClusterItem[]>(index.getClusters(boundsState, Math.floor(mapZoom)) as ClusterItem[]);

	$effect(() => {
		if (!map) return;
		const updateState = () => {
			if (!map) return;
			mapZoom = map.getZoom();
			const b = map.getBounds();
			boundsState = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
		};

		map.on('moveend', updateState);
		map.on('zoomend', updateState);
		map.on('load', updateState);

		updateState();

		return () => {
			map?.off('moveend', updateState);
			map?.off('zoomend', updateState);
			map?.off('load', updateState);
		};
	});

	$effect(() => {
		if (map) {
			map.fitBounds(bounds, {
				padding: 50,
				maxZoom: 10
			});
		}
	});

	export function jumpTo(viewState: ViewState): void {
		if (!map) return;
		map.jumpTo({
			center: flattenCoordinates(viewState.center),
			zoom: viewState.zoom,
			pitch: viewState.pitch,
			bearing: viewState.bearing
		});
	}

	function handleClusterClick(clusterId: number, coords: FlatCoordinates): void {
		if (!map) return;
		const featureCoordinates: Coordinates = lngLatToCoordinates(coords);
		const expansionZoom = index.getClusterExpansionZoom(clusterId);
		const zoom = Math.min(15, expansionZoom + 0.5);
		map.easeTo({
			center: flattenCoordinates(featureCoordinates),
			zoom: zoom,
			essential: true
		});
		viewState = {
			center: featureCoordinates,
			zoom: zoom,
			pitch: map.getPitch(),
			bearing: map.getBearing()
		};
	}

	function handleMarkerClick(props: FeatureProps, coords: FlatCoordinates): void {
		if (!map) return;
		const featureCoordinates: Coordinates = lngLatToCoordinates(coords);
		const targetCoordinates: Coordinates = snapToNearestMapCopy(featureCoordinates, {
			longitude: map.getCenter().lng,
			latitude: map.getCenter().lat
		});
		const zoom = 16;
		viewStateController?.add();
		popoverController?.openPopup('click', props, targetCoordinates);
		map.flyTo({
			center: flattenCoordinates(targetCoordinates),
			zoom: zoom,
			speed: 2.5,
			essential: true
		});
		viewState = {
			center: targetCoordinates,
			zoom: zoom,
			pitch: map.getPitch(),
			bearing: map.getBearing()
		};
	}

	function handlePopupClose(): void {
		const doAfter = () => viewStateController?.restore();
		popoverController?.closePopup(undefined, { doAfter: doAfter });
	}
</script>

<MapLibre
	bind:map
	class="@container-size relative z-0 h-full w-full overflow-hidden"
	style={MAP_LIBRE_STYLE}
	{bounds}
	maxBounds={lockToArea ? maxBounds : undefined}
	fitBoundsOptions={{
		padding: 50,
		maxZoom: 10
	}}
	minZoom={1}
	pitch={0}
	{scrollZoom}
	attributionControl={false}
	renderWorldCopies={true}
	onload={(e) => {
		assignMissingStyleImageResolver(e.target);
		popoverController = new PopupController<FeatureProps>();
		viewStateController = new ViewStateController(e.target);
	}}
	ondragstart={() => popoverController?.closePopup('click')}
>
	<NavigationControl showCompass={false} position="top-right" />
	<AttributionControl compact={true} position="bottom-right" />

	{#if popoverController}
		<ControllablePopup controller={popoverController} overrideHandleClose={handlePopupClose}>
			{#snippet ContentSnippet()}
				{const featureProps = $derived<FeatureProps | undefined>(popoverController!.popup.props)}
				{const popupContentProps = $derived.by<PopupContentProps | undefined>(() => {
					if (featureProps === undefined) {
						return undefined;
					}
					return {
						image: { filename: featureProps.imageUrl, IconOnNoImage: CITY_FEATURE_TYPE_ICON_MAP[CityFeatureType.CITY_DETAILS] },
						label: featureProps.label,
						cta: {
							label: 'Explore',
							href: featureProps.href
						},
						badges: featureProps.badges
					};
				})}
				{@render PopupContent(popupContentProps)}
			{/snippet}
		</ControllablePopup>
	{/if}

	{#each clusters as feature (feature.id || (feature.properties as FeatureProps).id)}
		{const isCluster = $derived<boolean>('cluster' in feature.properties && !!feature.properties.cluster)}

		{#if isCluster}
			{const props = $derived<ClusterProperties>(feature.properties as ClusterProperties)}
			{const clusterId = $derived<number>(props.cluster_id)}
			{const pointCount = $derived<number>(props.point_count)}
			<Marker
				lnglat={feature.geometry.coordinates as FlatCoordinates}
				onclick={() => handleClusterClick(clusterId, feature.geometry.coordinates as FlatCoordinates)}
			>
				{#snippet content()}
					<div
						class="relative flex size-10 cursor-pointer items-center justify-center rounded-full bg-primary font-black text-primary-content transition-transform hover:scale-110 md:size-12"
					>
						{pointCount}
					</div>
				{/snippet}
			</Marker>
		{:else}
			{const props = $derived<FeatureProps>(feature.properties as FeatureProps)}
			{const coords = $derived<FlatCoordinates>(feature.geometry.coordinates as FlatCoordinates)}
			{const Icon = $derived<Component>(CITY_FEATURE_TYPE_ICON_MAP[props.type])}
			<Marker lnglat={coords} onclick={() => handleMarkerClick(props, coords)}>
				{#snippet content()}
					<button
						class="relative flex size-10 cursor-pointer items-center justify-center rounded-full border-2 border-primary bg-neutral-900 text-xs font-extrabold text-white shadow-lg transition-transform hover:scale-110 md:size-12"
						aria-label={props.label}
						onmouseenter={() => popoverController?.openPopup('hover', props, lngLatToCoordinates(coords))}
						onmouseleave={() => popoverController?.closePopup('hover')}
					>
						{#if props.imageUrl}
							<img src={props.imageUrl} class="h-full w-full rounded-full object-cover" alt={props.label} draggable="false" />
						{:else}
							<div class="scale-110 opacity-90"><Icon /></div>
						{/if}
					</button>
				{/snippet}
			</Marker>
		{/if}
	{/each}
</MapLibre>
